if (!customElements.get('mix-match-selector')) {
  class MixMatchSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-mix-match-form]');
      this.lists = this.querySelectorAll('[data-product-list]');
      this.cards = this.querySelectorAll('[data-product-card]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorElement = this.querySelector('[data-form-error]');
      this.statusElement = this.querySelector('[data-form-status]');
      this.summary = this.querySelector('[data-summary]');
      this.summaryItems = this.querySelector('[data-summary-items]');
      this.summaryTotal = this.querySelector('[data-summary-total]');
      this.greeting = this.querySelector('greeting-field');
      this.galleryStage = this.closest('.js-product')?.querySelector('[data-mix-match-gallery-stage]');
      this.selectedOrder = [];
      this.pinnedCard = null;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.form.addEventListener('change', this.handleChange.bind(this));
      this.form.addEventListener('greeting:change', this.updateState.bind(this));

      this.cards.forEach((card) => {
        card.addEventListener('pointerenter', () => this.previewCard(card));
        card.addEventListener('pointerleave', () => this.restorePinnedGallery());
        card.addEventListener('focusin', () => this.previewCard(card));
        card.addEventListener('focusout', (evt) => {
          if (!card.contains(evt.relatedTarget)) this.restorePinnedGallery();
        });
      });

      this.updateState();
    }

    handleChange(evt) {
      if (evt.target.matches('[data-product-checkbox]')) {
        this.handleProductToggle(evt.target);
      } else if (evt.target.matches('[data-variant-select]')) {
        this.handleVariantChange(evt.target);
      }
    }

    handleProductToggle(checkbox) {
      const card = checkbox.closest('[data-product-card]');
      const variantWrapper = card.querySelector('[data-variant-wrapper]');
      const variantSelect = card.querySelector('[data-variant-select]');
      const token = card.dataset.cardToken;

      card.classList.toggle('is-selected', checkbox.checked);
      variantWrapper.hidden = !checkbox.checked || variantWrapper.hasAttribute('data-single-variant');
      variantSelect.disabled = !checkbox.checked;

      this.selectedOrder = this.selectedOrder.filter((selectedToken) => selectedToken !== token);
      if (checkbox.checked) {
        const selectedOption = variantSelect.selectedOptions[0];
        card.querySelector('[data-card-price]').textContent = selectedOption?.dataset.priceFormatted || card.dataset.defaultPrice;
        this.selectedOrder.push(token);
        this.pinnedCard = card;
        this.showCardGallery(card);
      } else if (this.pinnedCard === card) {
        this.pinnedCard = this.getLastSelectedCard();
        this.restorePinnedGallery();
      }

      if (!checkbox.checked) card.querySelector('[data-card-price]').textContent = card.dataset.defaultPrice;

      this.updateState();
    }

    handleVariantChange(select) {
      const card = select.closest('[data-product-card]');
      const selectedOption = select.selectedOptions[0];
      const price = selectedOption?.dataset.priceFormatted || card.dataset.defaultPrice;

      card.querySelector('[data-card-price]').textContent = price;
      this.selectedOrder = this.selectedOrder.filter((token) => token !== card.dataset.cardToken);
      this.selectedOrder.push(card.dataset.cardToken);
      this.pinnedCard = card;
      this.showCardGallery(card);
      this.updateState();
    }

    getLastSelectedCard() {
      for (let index = this.selectedOrder.length - 1; index >= 0; index -= 1) {
        const token = this.selectedOrder[index];
        const card = Array.from(this.cards).find(
          (candidate) => candidate.dataset.cardToken === token && candidate.querySelector('[data-product-checkbox]').checked
        );
        if (card) return card;
      }
      return null;
    }

    previewCard(card) {
      this.showGallery(card.dataset.productId, this.getCardMediaId(card));
    }

    showCardGallery(card) {
      this.showGallery(card.dataset.productId, this.getCardMediaId(card));
    }

    getCardMediaId(card) {
      return card.querySelector('[data-variant-select]')?.selectedOptions[0]?.dataset.mediaId || '';
    }

    restorePinnedGallery() {
      if (this.pinnedCard?.querySelector('[data-product-checkbox]').checked) {
        this.showCardGallery(this.pinnedCard);
      } else {
        this.showGallery(this.dataset.hostProductId);
      }
    }

    showGallery(productId, mediaId = '') {
      if (!this.galleryStage) return;
      const galleries = this.galleryStage.querySelectorAll('[data-mix-match-gallery]');
      const target = Array.from(galleries).find((gallery) => gallery.dataset.productId === String(productId));
      if (!target) return;

      galleries.forEach((gallery) => { gallery.hidden = gallery !== target; });
      const targetMediaId = mediaId || target.dataset.initialMediaId;

      window.requestAnimationFrame(() => {
        target.querySelectorAll('carousel-slider').forEach((carousel) => {
          if (typeof carousel.refresh === 'function') carousel.refresh();
        });
        if (targetMediaId && typeof target.setActiveMedia === 'function') {
          target.setActiveMedia(targetMediaId, false, true);
        }
      });
    }

    updateState() {
      this.lists.forEach((list) => this.updateList(list));

      const requiredList = this.querySelector('[data-list-index="1"]');
      const requiredCount = requiredList?.querySelectorAll('[data-product-checkbox]:checked').length || 0;
      const isValid = requiredCount >= Number(requiredList?.dataset.min || 1);
      const productItems = this.getSelectedProductItems();
      const greetingItems = this.getGreetingItems();

      this.submitButton.disabled = !isValid;
      this.statusElement.textContent = isValid
        ? `${productItems.length} produkt${productItems.length === 1 ? '' : 'er'} valgt.`
        : 'Vælg mindst ét produkt fra den første liste.';
      this.updateSummary(productItems, greetingItems);
    }

    updateList(list) {
      const selectedCount = list.querySelectorAll('[data-product-checkbox]:checked').length;
      const min = Number(list.dataset.min);
      const max = Number(list.dataset.max);
      const status = list.querySelector('[data-list-status]');

      if (min > 0) {
        status.textContent = `${selectedCount} valgt · Vælg mellem ${min} og ${max}`;
      } else {
        status.textContent = `${selectedCount} valgt · Vælg op til ${max}`;
      }

      list.querySelectorAll('[data-product-card]').forEach((card) => {
        const checkbox = card.querySelector('[data-product-checkbox]');
        if (!checkbox.checked) checkbox.disabled = card.hasAttribute('data-sold-out') || selectedCount >= max;
      });
    }

    getSelectedProductItems() {
      return Array.from(this.cards)
        .filter((card) => card.querySelector('[data-product-checkbox]').checked)
        .map((card) => {
          const selectedOption = card.querySelector('[data-variant-select]').selectedOptions[0];
          return {
            id: Number(selectedOption.value),
            quantity: 1,
            title: card.dataset.productTitle,
            variantTitle: selectedOption.dataset.variantTitle,
            price: Number(selectedOption.dataset.price),
            listIndex: Number(card.dataset.listIndex)
          };
        });
    }

    getGreetingItems() {
      if (this.greeting && typeof this.greeting.getSelectedItems === 'function') {
        return this.greeting.getSelectedItems();
      }
      return [];
    }

    updateSummary(productItems, greetingItems) {
      const items = [...productItems, ...greetingItems];
      this.summary.hidden = items.length === 0;
      this.summaryItems.innerHTML = '';

      items.forEach((item) => {
        const row = document.createElement('div');
        const title = document.createElement('span');
        const price = document.createElement('span');
        const showVariant = item.variantTitle && item.variantTitle.toLowerCase() !== 'default title';

        row.className = 'mix-match-summary__item';
        title.className = 'mix-match-summary__item-title';
        title.textContent = `${item.title}${showVariant ? ` — ${item.variantTitle}` : ''}`;
        price.textContent = this.formatMoney(item.price);
        row.append(title, price);
        this.summaryItems.append(row);
      });

      const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      this.summaryTotal.textContent = this.formatMoney(total);
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '{{ amount }}';
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!placeholder) return `${(cents / 100).toFixed(2)} kr.`;

      const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
        const number = Number(value);
        if (!Number.isFinite(number)) return '0';
        const parts = (number / 100).toFixed(precision).split('.');
        parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
      };

      let value;
      switch (placeholder[1]) {
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }
      return format.replace(placeholder[0], value);
    }

    getCartItems(productItems, greetingItems) {
      const groupedProducts = new Map();
      productItems.forEach((item) => {
        const existing = groupedProducts.get(item.id);
        if (existing) {
          existing.quantity += 1;
        } else {
          groupedProducts.set(item.id, { id: item.id, quantity: 1 });
        }
      });

      const gifts = greetingItems.map((item) => {
        const gift = { id: item.id, quantity: item.quantity };
        if (item.properties) gift.properties = item.properties;
        return gift;
      });
      return [...groupedProducts.values(), ...gifts];
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      const productItems = this.getSelectedProductItems();
      const greetingItems = this.getGreetingItems();
      if (productItems.filter((item) => item.listIndex === 1).length < 1) {
        this.updateState();
        return;
      }
      if (this.greeting && typeof this.greeting.reportValidity === 'function' && !this.greeting.reportValidity()) return;

      this.setError();
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('is-loading');

      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map((selector) => document.querySelector(selector))
        .filter(Boolean);
      const sectionIds = sectionsToUpdate.map((element) => element.dataset.sectionId).filter(Boolean);
      const payload = { items: this.getCartItems(productItems, greetingItems) };
      if (sectionIds.length > 0) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }
      if (this.greeting && typeof this.greeting.getAttributes === 'function') {
        const attributes = this.greeting.getAttributes();
        if (Object.keys(attributes).length > 0) payload.attributes = attributes;
      }

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(this.getErrorMessage(data));

        this.handleSuccessfulAdd(data, sectionsToUpdate, productItems[0]);
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: productItems[0].id }
        }));

        this.submitButton.classList.add('is-success');
        this.submitButton.removeAttribute('aria-disabled');
        window.setTimeout(() => {
          this.submitButton.classList.remove('is-loading', 'is-success');
          this.updateState();
        }, 2000);
      } catch (error) {
        this.setError(error.message || 'Produkterne kunne ikke lægges i kurven. Prøv igen.');
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: this.errorElement.textContent }
        }));
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        this.submitButton.classList.remove('is-loading', 'is-success');
        this.submitButton.removeAttribute('aria-disabled');
        this.updateState();
      }
    }

    getErrorMessage(data) {
      if (typeof data.description === 'string') return data.description;
      if (typeof data.message === 'string') return data.message;
      if (data.errors && typeof data.errors === 'object') {
        return Object.values(data.errors).flat().join(', ');
      }
      return 'Produkterne kunne ikke lægges i kurven. Prøv igen.';
    }

    handleSuccessfulAdd(data, sectionsToUpdate, firstProduct) {
      if (document.querySelector('.template-cart')) {
        const cartForm = document.querySelector('cart-form');
        if (cartForm?.enableAjaxUpdate) cartForm.refresh();
        return;
      }

      if (theme.settings.afterAddToCart === 'page' || theme.settings.afterAddToCart === 'no-js') {
        window.setTimeout(() => { window.location.href = theme.routes.cart; }, 300);
        return;
      }

      const renderedSections = data.sections || {};
      let sectionsUpdated = sectionsToUpdate.length > 0;
      sectionsToUpdate.forEach((element) => {
        const sectionMarkup = renderedSections[element.dataset.sectionId];
        if (sectionMarkup && typeof element.updateFromCartChange === 'function') {
          element.updateFromCartChange(sectionMarkup);
        } else {
          sectionsUpdated = false;
        }
      });
      if (!sectionsUpdated) this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));

      if (theme.settings.afterAddToCart === 'notification') {
        const template = document.getElementById('AddedNotification');
        if (template) {
          const notification = template.content.firstElementChild.cloneNode(true);
          notification.dataset.productTitle = firstProduct.title;
          (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
        }
      } else if (theme.settings.afterAddToCart === 'drawer') {
        document.querySelector('.js-cart-drawer')?.open();
      }
    }

    setError(message = '') {
      this.errorElement.hidden = !message;
      this.errorElement.textContent = message;
    }
  }

  customElements.define('mix-match-selector', MixMatchSelector);
}
